import React, { FC, PropsWithChildren, useMemo, useCallback } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Block } from '../../../Availabilities/Time'
import CoreText from 'components/Core/CoreText'
import { Event } from '../../../types'
import Slot, { SlotBlock } from './Slot'
import styles from './styles.module.scss'

dayjs.extend(utc)
dayjs.extend(timezone)

const START_TIME = 7
const END_TIME = 18

interface SlotsProps {
  event: Event
  className?: string
  selectedDate: Date
  timezone: string
  adjustedNow: Date
  availabilities: Block[]
  events: Event[]
  onChange: (start: Date, end: Date, selected: boolean) => void
}

const Slots: FC<PropsWithChildren<SlotsProps>> = ({
  event,
  className,
  selectedDate,
  timezone,
  adjustedNow,
  availabilities,
  events,
  onChange,
}) => {
  const djsSelectedDate = useMemo(() => {
    return dayjs(selectedDate).tz(timezone)
  }, [selectedDate, timezone])

  const startOfDay = useMemo(() => {
    return djsSelectedDate.startOf('day').add(START_TIME, 'hours')
  }, [djsSelectedDate])

  const endOfDay = useMemo(() => {
    return djsSelectedDate.startOf('day').add(END_TIME, 'hours')
  }, [djsSelectedDate])

  const todaysAvailabilities = useMemo(() => {
    const iStartOfDay = startOfDay.toDate().getTime()
    const iEndOfDay = endOfDay.toDate().getTime()
    // Truncate all availability blocks to our start and end of day.
    return availabilities.reduce((all, block) => {
      let start = block.start.getTime()
      let end = block.end.getTime()

      if (iEndOfDay <= start) return all
      if (iStartOfDay >= end) return all

      start = Math.max(iStartOfDay, start)
      end = Math.min(iEndOfDay, end)

      if (start >= end) return all
      return [...all, { start: new Date(start), end: new Date(end) }]
    }, [] as Block[])
  }, [startOfDay, endOfDay, availabilities])

  const allAvailabilities = useMemo(() => {
    // Create an availability for each hour of the day.
    // Mark them as selected if they are entirely covered by
    // Today's availability.
    // If partially covered, split it up
    const raw = new Array(END_TIME - START_TIME).fill(null).reduce((all, _, i) => {
      const start = startOfDay.add(i, 'hours').toDate().getTime()
      const end = startOfDay
        .add(i + 1, 'hours')
        .toDate()
        .getTime()

      let newBlocks: SlotBlock[] = []

      const taken = events
        .filter((event) => {
          const eStart = new Date(event.start_time as string)
          const eEnd = new Date(event.end_time as string)
          return eEnd.getTime() > start && eStart.getTime() < end
        })
        .map((event) => ({
          event: event,
          start: new Date(event.start_time as string),
          end: new Date(event.end_time as string),
        }))

      if (taken.length > 0) {
        if (start < taken[0].start.getTime()) {
          newBlocks.push({ start: new Date(start), end: taken[0].start, selected: false, event: null })
        }
        newBlocks = [
          ...newBlocks,
          ...taken.map((event) => ({
            start: new Date(Math.max(event.start.getTime(), start)),
            end: new Date(Math.min(event.end.getTime(), end)),
            selected: false,
            event: event.event,
          })),
        ]
        if (end > taken[0].end.getTime()) {
          newBlocks.push({ start: taken[0].end, end: new Date(end), selected: false, event: null })
        }
      } else {
        newBlocks.push({ start: new Date(start), end: new Date(end), selected: false, event: null })
      }

      const selected = todaysAvailabilities.filter((block) => {
        return block.end.getTime() > start && block.start.getTime() < end
      })

      if (selected.length > 0) {
        newBlocks = newBlocks.reduce((nbAll, newBlock) => {
          // Any new block that has an event, doesn't need to be selected.
          if (newBlock.event) return [...nbAll, newBlock]
          // Any new block that is out of bounds from our select can be ignored
          if (
            newBlock.end.getTime() <= selected[0].start.getTime() ||
            newBlock.start.getTime() >= selected.slice(-1)[0].end.getTime()
          ) {
            return [...nbAll, newBlock]
          }

          if (newBlock.start.getTime() < selected[0].start.getTime()) {
            nbAll = [...nbAll, { start: newBlock.start, end: selected[0].start, selected: false, event: null }]
          }
          nbAll = [
            ...nbAll,
            ...selected.reduce((fromMatched, match) => {
              const start = Math.max(match.start.getTime(), newBlock.start.getTime())
              const end = Math.min(match.end.getTime(), newBlock.end.getTime())
              // Don't include it if it is empty.
              if (start == end) return fromMatched
              return [
                ...fromMatched,
                {
                  start: new Date(start),
                  end: new Date(end),
                  selected: true,
                  event: null,
                },
              ]
            }, [] as SlotBlock[]),
          ]
          if (newBlock.end.getTime() > selected.slice(-1)[0].end.getTime()) {
            nbAll = [...nbAll, { start: selected.slice(-1)[0].end, end: newBlock.end, selected: false, event: null }]
          }
          return nbAll
        }, [] as SlotBlock[])
      }

      return [...all, ...newBlocks]
    }, [] as SlotBlock[])

    // Any events that are split up, but next to each other can be combined.
    return raw.reduce((all, block) => {
      if (!all.length) return [block]

      const last = all.slice(-1)[0]
      const rest = all.slice(0, -1)
      if (block.event && block.event?.id == last.event?.id) {
        return [...rest, { ...last, end: block.end }]
      }
      return [...all, block]
    }, [] as SlotBlock[])
  }, [todaysAvailabilities, startOfDay, events])

  const handleSlotClick = useCallback(
    (block: SlotBlock) => {
      if (block.event) return
      onChange(block.start, block.end, !block.selected)
    },
    [onChange]
  )

  return (
    <div className={cx(styles.slots, className)}>
      <div className={styles.selectedDate}>
        <CoreText.Paragraph className="tw:mb-1">
          {`${djsSelectedDate.format('ddd').toUpperCase()}, ${djsSelectedDate.format('D MMM')}`}
        </CoreText.Paragraph>
        <CoreText.Paragraph
          className={cx({ 'tw:underline': timezone != 'America/Los_Angeles' })}
          weight="light"
          color="tertiaryBlack"
        >
          {djsSelectedDate.format('zzz')}
        </CoreText.Paragraph>
      </div>
      <ul className={styles.timeSlots}>
        {allAvailabilities.map((block) => (
          <Slot
            key={block.start.toString()}
            event={event}
            block={block}
            timezone={timezone}
            adjustedNow={adjustedNow}
            onClick={handleSlotClick}
          />
        ))}
      </ul>
    </div>
  )
}

export default Slots
